import React, { useContext, useMemo, useState } from 'react'
import { BsShieldCheck, BsSend } from 'react-icons/bs'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import AuthContext from '@contexts/Auth'
import * as $Pregnancy from '@services/Pregnancy'

const SweetAlert = withReactContent(Swal)

type OrderSelection = number | 'other' | ''

const PregnancyReportForm: React.FC = () => {
  const { inProgressOrders } = useContext(AuthContext)

  const [selectedOrderId, setSelectedOrderId] = useState<OrderSelection>('')
  const [manualOrderNumber, setManualOrderNumber] = useState('')
  const [manualTreatment, setManualTreatment] = useState('')
  const [manualDonor, setManualDonor] = useState('')
  const [manualClinic, setManualClinic] = useState('')
  const [result, setResult] = useState('')
  const [birthDate, setBirthDate] = useState('')
  const [boys, setBoys] = useState('')
  const [girls, setGirls] = useState('')
  const [hasFrozenEmbryos, setHasFrozenEmbryos] = useState('')
  const [comment, setComment] = useState('')

  const isOther = selectedOrderId === 'other'

  const selectedOrder = useMemo(
    () => (typeof selectedOrderId === 'number' ? inProgressOrders.find(o => o.id === selectedOrderId) ?? null : null),
    [inProgressOrders, selectedOrderId]
  )

  const treatmentType = isOther ? manualTreatment : (selectedOrder?.treatment_type ?? '')
  const donorNumber = isOther
    ? manualDonor
    : (selectedOrder?.order_donor?.internal_id ?? selectedOrder?.selected_donor?.internal_id ?? '')
  const clinicName = isOther
    ? manualClinic
    : (selectedOrder?.clinic?.trading_name ?? selectedOrder?.clinic?.company_name ?? '')

  const resetForm = () => {
    setSelectedOrderId('')
    setManualOrderNumber('')
    setManualTreatment('')
    setManualDonor('')
    setManualClinic('')
    setResult('')
    setBirthDate('')
    setBoys('')
    setGirls('')
    setHasFrozenEmbryos('')
    setComment('')
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!selectedOrderId) {
      SweetAlert.fire({ title: 'Atenção', text: 'Selecione o pedido ou escolha "Outro pedido".', icon: 'warning', confirmButtonText: 'Ok' })
      return
    }

    if (isOther && !manualOrderNumber.trim()) {
      SweetAlert.fire({ title: 'Atenção', text: 'Informe o número do pedido.', icon: 'warning', confirmButtonText: 'Ok' })
      return
    }

    if (!result) {
      SweetAlert.fire({ title: 'Atenção', text: 'Selecione o resultado.', icon: 'warning', confirmButtonText: 'Ok' })
      return
    }

    const payload = {
      order_id: isOther || String(selectedOrderId) === '' ? null : selectedOrderId,
      manual_order_number: isOther ? manualOrderNumber : null,
      manual_treatment: isOther ? manualTreatment || null : null,
      manual_donor: isOther ? manualDonor || null : null,
      manual_clinic: isOther ? manualClinic || null : null,
      result,
      birth_date: birthDate || null,
      boys: boys === '' ? null : Number(boys),
      girls: girls === '' ? null : Number(girls),
      has_frozen_embryos: hasFrozenEmbryos || null,
      comment: comment.trim() || null,
    }

    SweetAlert.fire({
      title: 'Aguarde...',
      text: 'Enviando relato',
      icon: 'info',
      allowOutsideClick: false,
      allowEscapeKey: false,
      allowEnterKey: false,
      didOpen: () => SweetAlert.showLoading(),
    })

    $Pregnancy.submitReport(payload)
      .then(() => {
        SweetAlert.fire({
          title: 'Sucesso!',
          text: 'Relato enviado com sucesso!',
          icon: 'success',
          confirmButtonText: 'Ok',
        }).then(() => resetForm())
      })
      .catch((err: any) => {
        SweetAlert.fire({
          title: 'Erro!',
          text: err?.response?.data?.message || 'Ocorreu um erro ao enviar seu relato.',
          icon: 'error',
          confirmButtonText: 'Ok',
        })
      })
  }

  return (
    <form className="rounded border bg-white" onSubmit={handleSubmit}>

      {/* Section 1 - Order & Treatment */}
      <div className="p-4">
        <div className="mb-4">
          <h6 className="font-weight-bold mb-1">Dados do pedido e tratamento</h6>
          <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
            Preencha as informações sobre seu pedido e tipo de tratamento realizado.
          </p>
        </div>
        <div className="row">
          <div className="col-12 col-sm-4 form-group">
            <label htmlFor="order-number">
              Qual nº do seu pedido <span className="text-danger">*</span>
            </label>
            <select
              id="order-number"
              className="form-control"
              value={selectedOrderId}
              onChange={e => {
                const v = e.target.value
                if (v === '') setSelectedOrderId('')
                else if (v === 'other') setSelectedOrderId('other')
                else setSelectedOrderId(Number(v))
              }}
            >
              <option value="">Selecione</option>
              {inProgressOrders.map(order => (
                <option key={order.id} value={order.id}>
                  {order.internal_id}
                </option>
              ))}
              <option value="other">Outro pedido (pedido legado)</option>
            </select>
          </div>
          {isOther && (
            <div className="col-12 col-sm-4 form-group">
              <label htmlFor="manual-order-number">
                Digite o nº do pedido <span className="text-danger">*</span>
              </label>
              <input
                id="manual-order-number"
                type="text"
                className="form-control"
                value={manualOrderNumber}
                onChange={e => setManualOrderNumber(e.target.value)}
                placeholder="Ex: PRO-1234"
              />
            </div>
          )}
          <div className="col-12 col-sm-4 form-group">
            <label htmlFor="treatment-type">
              Tipo de tratamento <span className="text-danger">*</span>
            </label>
            <input
              id="treatment-type"
              type="text"
              className="form-control"
              value={treatmentType}
              placeholder={isOther ? 'Informe o tratamento' : (selectedOrder ? '-' : 'Selecione um pedido')}
              readOnly={!isOther}
              disabled={!isOther && !selectedOrder}
              onChange={e => isOther && setManualTreatment(e.target.value)}
            />
          </div>
          <div className="col-12 col-sm-4 form-group">
            <label htmlFor="donor-number">
              Número do doador <span className="text-danger">*</span>
            </label>
            <input
              id="donor-number"
              type="text"
              className="form-control"
              value={donorNumber}
              placeholder={isOther ? 'Informe o doador' : (selectedOrder ? '-' : 'Selecione um pedido')}
              readOnly={!isOther}
              disabled={!isOther && !selectedOrder}
              onChange={e => isOther && setManualDonor(e.target.value)}
            />
          </div>
          <div className="col-12 col-sm-6 form-group">
            <label htmlFor="clinic">
              Clínica onde fez tratamento <span className="text-danger">*</span>
            </label>
            <input
              id="clinic"
              type="text"
              className="form-control"
              value={clinicName}
              placeholder={isOther ? 'Informe a clínica' : (selectedOrder ? '-' : 'Selecione um pedido')}
              readOnly={!isOther}
              disabled={!isOther && !selectedOrder}
              onChange={e => isOther && setManualClinic(e.target.value)}
            />
          </div>
          <div className="col-12 col-sm-6 form-group">
            <label htmlFor="result">
              Resultado <span className="text-danger">*</span>
            </label>
            <select
              id="result"
              className="form-control"
              value={result}
              onChange={e => setResult(e.target.value)}
            >
              <option value="">Selecione</option>
              <option value="positivo_nasceu">Positivo - Nasceu</option>
              <option value="positivo_gestacao">Positivo - Em gestação</option>
              <option value="negativo">Negativo</option>
              <option value="aborto">Aborto espontâneo</option>
            </select>
          </div>
        </div>
      </div>

      <hr className="m-0" />

      {/* Section 2 - Birth Details */}
      <div className="p-4">
        <div className="mb-4">
          <h6 className="font-weight-bold mb-1">Detalhes do nascimento</h6>
          <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
            Informe os detalhes sobre a clínica e o nascimento.
          </p>
        </div>
        <div className="row">
          <div className="col-12 col-sm-3 form-group">
            <label htmlFor="birth-date">
              Data de nascimento <span className="text-danger">*</span>
            </label>
            <input
              id="birth-date"
              type="date"
              className="form-control"
              value={birthDate}
              onChange={e => setBirthDate(e.target.value)}
            />
          </div>
          <div className="col-6 col-sm-3 form-group">
            <label htmlFor="boys">
              Nº de meninos <span className="text-danger">*</span>
            </label>
            <select
              id="boys"
              className="form-control"
              value={boys}
              onChange={e => setBoys(e.target.value)}
            >
              <option value="">Selecione</option>
              <option value="0">0</option>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="5">5</option>
            </select>
          </div>
          <div className="col-6 col-sm-3 form-group">
            <label htmlFor="girls">
              Nº de meninas <span className="text-danger">*</span>
            </label>
            <select
              id="girls"
              className="form-control"
              value={girls}
              onChange={e => setGirls(e.target.value)}
            >
              <option value="">Selecione</option>
              <option value="0">0</option>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="5">5</option>
            </select>
          </div>
          <div className="col-12 col-sm-3 form-group">
            <label htmlFor="frozen-embryos">
              Possui embriões congelados? <span className="text-danger">*</span>
            </label>
            <select
              id="frozen-embryos"
              className="form-control"
              value={hasFrozenEmbryos}
              onChange={e => setHasFrozenEmbryos(e.target.value)}
            >
              <option value="">Selecione</option>
              <option value="sim">Sim</option>
              <option value="nao">Não</option>
            </select>
          </div>
        </div>
      </div>

      <hr className="m-0" />

      {/* Section 3 - Comments */}
      <div className="p-4">
        <div className="mb-4">
          <h6 className="font-weight-bold mb-1">Comentário</h6>
          <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
            Deixe uma mensagem adicional se desejar (opcional).
          </p>
        </div>
        <textarea
          className="form-control"
          rows={4}
          placeholder="Escreva seu comentário aqui..."
          style={{ resize: 'vertical' }}
          value={comment}
          onChange={e => setComment(e.target.value)}
        />
      </div>

      <hr className="m-0" />

      {/* Footer - Privacy + Submit */}
      <div className="p-4 d-flex flex-column flex-md-row align-items-md-center justify-content-md-between" style={{ gap: '1.5rem' }}>
        <div className="d-flex align-items-start" style={{ gap: '0.75rem' }}>
          <BsShieldCheck size={18} color="#FA5A84" style={{ flexShrink: 0, marginTop: '2px' }} />
          <p className="mb-0 text-muted text-justify" style={{ fontSize: '0.75rem', lineHeight: '1.6' }}>
            Não se preocupe, todas as informações fornecidas ao Pro-Seed são confidenciais e nos ajudarão a
            contabilizar e acompanhar o número de nascimentos por doador em cada região. Se ainda estiver em
            gestação, sinta-se à vontade para voltar a esse espaço para nos atualizar do nascimento!
          </p>
        </div>
        <button type="submit" className="btn btn-primary d-flex align-items-center justify-content-center mb-0" style={{ gap: '0.5rem', minWidth: '200px', whiteSpace: 'nowrap' }}>
          <BsSend size={14} />
          Enviar
        </button>
      </div>

    </form>
  )
}

export default PregnancyReportForm
